import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Alert, Table, Badge, ProgressBar } from "react-bootstrap";
import NowDayTaskList from "./NowDayTaskList";
import FutureTaskList from "./FutureTaskList";
import TaskDailyModal from "../Taskdailly/TaskDailyModal";
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api";

const MyWorkDashboard = () => {
  const [kpis, setKpis] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [taskJDs, setTaskJDs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalInitialData, setModalInitialData] = useState({});
  const [operationMsg, setOperationMsg] = useState({ show: false, text: "", variant: "success" });

  const dateConfig = useMemo(() => {
    const now = new Date();
    return {
      todayISO: now.toISOString().split('T')[0],
      tomorrowISO: new Date(new Date().setDate(now.getDate() + 1)).toISOString().split('T')[0],
      display: new Date().toLocaleDateString("en-GB"),
    };
  }, []);

  const showAlert = (text, variant = "success") => {
    setOperationMsg({ show: true, text, variant });
    setTimeout(() => setOperationMsg({ show: false, text: "", variant: "success" }), 3000);
  };

  const fetchData = useCallback(async () => {
    try {
      const [resTasks, resJDs, resKpis] = await Promise.all([
        axiosClient.get(API_ENDPOINTS.taskdailies),
        axiosClient.get(API_ENDPOINTS.taskjds),
        axiosClient.get(API_ENDPOINTS.kpis)
      ]);
      setAllTasks(resTasks.data || []);
      setTaskJDs(resJDs.data || []);
      setKpis(resKpis.data || []);
    } catch (e) {
      console.error("Fetch Error:", e);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleEdit = (task) => {
    setModalInitialData(task);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this task?")) {
      try {
        await axiosClient.delete(`${API_ENDPOINTS.taskdailies}/${id}`);
        fetchData();
        showAlert("Task deleted successfully!", "success");
      } catch (err) {
        showAlert("Failed to delete task.", "danger");
      }
    }
  };

  const currentUid = String(localStorage.getItem("userId") || "");

  // --- Filter & Processing Logic ---
  const { todayTasks, futureTasks } = useMemo(() => {
    const today = [];
    const future = [];
    allTasks.forEach((t) => {
      if (String(t.UserId || t.userId) !== currentUid) return;
      const tDate = String(t.WorkDate || t.workDate || "").substring(0, 10);
      if (tDate === dateConfig.todayISO) today.push(t);
      else if (tDate > dateConfig.todayISO) future.push(t);
    });
    return { todayTasks: today, futureTasks: future };
  }, [allTasks, currentUid, dateConfig.todayISO]);

  const myActiveKPIs = useMemo(() => {
    return kpis.filter(k => {
      const isMyKpi = String(k.UserId || k.userId) === currentUid;
      const endDate = String(k.EndDate || k.endDate || "").substring(0, 10);
      return isMyKpi && endDate >= dateConfig.todayISO;
    }).map(k => {
      const target = parseFloat(k.targetValue || k.TargetValue) || 0;
      const achieved = parseFloat(k.achievedValue || k.AchievedValue) || 0;
      const progress = target > 0 ? Math.min((achieved / target) * 100, 100) : 0;
      return { ...k, progress };
    });
  }, [kpis, currentUid, dateConfig.todayISO]);

  const hasDoneToday = todayTasks.some(t => (t.Status || t.status) === "Done");
  const hasFuturePlan = futureTasks.length > 0;
  const totalHours = todayTasks.reduce((sum, t) => sum + (parseFloat(t.Hours || t.hours) || 0), 0);

  return (
    <div className="container-fluid pb-5 mt-3" style={{ backgroundColor: "#f8f9fc" }}>
      <div className="d-sm-flex align-items-center justify-content-between mb-4 pt-3">
        <h1 className="h4 mb-0 text-gray-800 fw-bold text-uppercase">Daily Dashboard</h1>
        <div className="bg-white border rounded-pill px-3 py-1 shadow-sm text-primary small fw-bold">
          Date: {dateConfig.display}
        </div>
      </div>

      {operationMsg.show && (
        <Alert variant={operationMsg.variant} className="position-fixed top-0 start-50 translate-middle-x mt-3 shadow-lg" style={{ zIndex: 9999 }}>
          {operationMsg.text}
        </Alert>
      )}

      {/* TODAY SECTION */}
      <Alert variant={hasDoneToday ? "success" : "danger"} className="border-0 shadow-sm py-2 mb-3">
        <i className={`fas ${hasDoneToday ? "fa-check-circle" : "fa-exclamation-triangle"} me-2`}></i>
        {hasDoneToday 
          ? <span><strong>Success!</strong> You have at least one completed task.</span> 
          : <span><strong>Alert:</strong> No tasks marked as <b>Done</b> for today!</span>
        }
      </Alert>

      <div className={`card shadow-sm mb-4 border-0 border-start border-5 ${hasDoneToday ? "border-success" : "border-danger"}`}>
        <div className="card-header py-3 bg-white d-flex justify-content-between align-items-center border-0">
          <h6 className="m-0 font-weight-bold text-muted small text-uppercase">Today's Progress</h6>
          <div className="d-flex align-items-center gap-3">
            <span className={`badge rounded-pill ${hasDoneToday ? "bg-success" : "bg-danger"}`}>
              {totalHours.toFixed(2)}h
            </span>
            <button 
              className="btn btn-sm btn-success fw-bold px-3 rounded-pill shadow-sm"
              onClick={() => {
                setModalInitialData({ workDate: dateConfig.todayISO, status: "Done" });
                setShowModal(true);
              }}
            >
              <i className="fas fa-plus me-1"></i> LOG TASK
            </button>
          </div>
        </div>
        <div className="card-body p-0">
          <NowDayTaskList data={todayTasks} taskJDs={taskJDs} onEdit={handleEdit} onDelete={handleDelete} />
        </div>
      </div>

      {/* FUTURE SECTION */}
      <Alert variant={hasFuturePlan ? "success" : "danger"} className="border-0 shadow-sm py-2 mb-3">
        <i className={`fas ${hasFuturePlan ? "fa-calendar-check" : "fa-calendar-times"} me-2`}></i>
        {hasFuturePlan 
          ? <span><strong>Planned!</strong> You have scheduled {futureTasks.length} tasks for the future.</span> 
          : <span><strong>Warning:</strong> No future plans scheduled yet!</span>
        }
      </Alert>

      <div className={`card shadow-sm mb-4 border-0 border-start border-5 ${hasFuturePlan ? "border-success" : "border-danger"}`}>
        <div className="card-header py-3 bg-white d-flex justify-content-between align-items-center border-0">
          <h6 className="m-0 font-weight-bold text-muted small text-uppercase">Future Plans</h6>
          <button 
            className="btn btn-sm btn-primary fw-bold px-3 rounded-pill shadow-sm"
            onClick={() => {
              setModalInitialData({ workDate: dateConfig.tomorrowISO, status: "Scheduling" });
              setShowModal(true);
            }}
          >
            <i className="fas fa-calendar-plus me-1"></i> ADD PLAN
          </button>
        </div>
        <div className="card-body p-0">
          <FutureTaskList data={futureTasks} taskJDs={taskJDs} onEdit={handleEdit} onDelete={handleDelete} />
        </div>
      </div>

      {/* KPI SECTION - Updated Columns & Style */}
      <div className="card shadow-sm mb-4 border-0 border-start border-5 border-info">
        <div className="card-header py-3 bg-white d-flex justify-content-between align-items-center border-0">
          <h6 className="m-0 font-weight-bold text-muted small text-uppercase">Your Active KPIs</h6>
          <Badge bg="info" className="rounded-pill px-3">{myActiveKPIs.length} KPIs</Badge>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <Table hover align="middle" className="mb-0" style={{ fontSize: '0.75rem' }}>
              <thead className="bg-light text-muted text-uppercase">
                <tr>
                  <th className="py-3">No.</th>
                  <th className="py-3">KPI Code</th>
                  <th className="py-3">Description</th>
                  <th className="py-3">Unit</th>
                  <th className="py-3">Target</th>
                  <th className="py-3">Achieved</th>
                  <th className="py-3" style={{ minWidth: '150px' }}>Progress (%)</th>
                </tr>
              </thead>
              <tbody>
                {myActiveKPIs.length > 0 ? (
                  myActiveKPIs.map((k, idx) => (
                    <tr key={k.id || idx}>
                      <td className="text-muted">{idx + 1}</td>
                      <td className="fw-bold text-primary">{k.kpiCode}</td>
                      <td style={{ maxWidth: '250px' }} className="text-wrap">{k.description}</td>
                      <td>{k.unit}</td>
                      <td className="fw-bold">{k.targetValue}</td>
                      <td className="fw-bold text-success">{k.achievedValue}</td>
                      <td>
                        <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                          <div className="flex-grow-1">
                            <ProgressBar 
                              now={k.progress} 
                              variant={k.progress >= 100 ? "success" : "info"} 
                              style={{ height: '6px' }} 
                            />
                          </div>
                          <span className="fw-bold text-muted" style={{ fontSize: '10px', minWidth: '35px' }}>
                            {Math.round(k.progress)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-5 text-muted small italic">
                      <i className="fas fa-chart-line d-block mb-2 fa-2x opacity-25"></i>
                      No active KPIs found for current period.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>

      <TaskDailyModal 
        show={showModal}
        onClose={() => setShowModal(false)}
        onSave={async (p) => {
          try {
            if (p.id) {
              await axiosClient.put(`${API_ENDPOINTS.taskdailies}/${p.id}`, p);
              showAlert("Task updated successfully!");
            } else {
              await axiosClient.post(API_ENDPOINTS.taskdailies, p);
              showAlert("New task added!");
            }
            setShowModal(false);
            fetchData();
          } catch (err) {
            showAlert("Action failed.", "danger");
          }
        }}
        taskJDs={taskJDs}
        kpis={kpis}
        initialData={modalInitialData}
      />
    </div>
  );
};

export default MyWorkDashboard;